'use client'
import { useSyncExternalStore } from 'react'
import {
  type DataTag,
  type QueryKey,
  type QueryClient,
  notifyManager,
  useQueryClient,
} from '@tanstack/react-query'

type Listener = () => void

type AnyQueryOptions = { queryKey: QueryKey }

// infers the cached data type from the DataTag that queryOptions() brands
// onto the queryKey. this is the same mechanism queryClient.getQueryData()
// uses, so consumers get the exact type without a second generic or fragile
// queryFn return inference.
type InferQueryData<TOptions extends AnyQueryOptions> =
  TOptions['queryKey'] extends DataTag<unknown, infer TData, unknown> ? TData : unknown

/**
 * a lightweight external store exposing only the cached query data.
 * - does NOT create a QueryObserver and does NOT create/fetch the query.
 * - query ownership (fetching, invalidation) belongs to single controller
 *   mounted below each page's HydrationBoundary.
 * - all consumers share ONE QueryCache subscription per QueryClient,
 *   instead of mounting a QueryObserver per consumer via useQuery.
 * - react notifications are scheduled and coalesced because an app router
 *   HydrationBoundary can write new queries to the cache during render.
 * - getOptions receives the hook's arguments, so keys can depend on them:
 *   createPassiveQueryStore(() => userQueryOptions) or createPassiveQueryStore(featureFlagsQueryOptions).
 */
export const createPassiveQueryStore = <TArgs extends unknown[], TOptions extends AnyQueryOptions>(
  getOptions: (...args: TArgs) => TOptions,
) => {
  type TData = InferQueryData<TOptions>

  const createStore = (queryClient: QueryClient, queryHash: string) => {
    const queryCache = queryClient.getQueryCache()

    const listeners = new Set<Listener>()
    let unsubscribeFromQueryCache: (() => void) | undefined
    let notificationScheduled = false

    // cache lookup by precomputed hash and there is no re hashing on every read whereas
    // queryClient.getQueryData(options) would rehash the key on every read.
    const getSnapshot = () => queryCache.get<TData>(queryHash)?.state.data

    let lastNotifiedSnapshot = getSnapshot()

    const scheduleNotification = () => {
      // coalesce a synchronous burst of cache events into one react notification.
      // QueryCache subscriptions fire synchronously, so multiple events can land in
      // quick succession. notifyManager.schedule() defers its callback to the next
      // tick (defaultScheduler is setTimeout(cb, 0)), so the notificationScheduled
      // guard collapses that burst into a single deferred flush. this is more of an
      // optimization.
      if (notificationScheduled) return
      notificationScheduled = true

      // HydrationBoundary can add new queries to the cache while it is rendering in the
      // app router. QueryCache subscriptions fire synchronously, so notifying react listeners
      // inline could update an already mounted consumer while HydrationBoundary is still
      // rendering which causes "To locate the bad setState() call inside HydrationBoundary" error.
      // instead, we should use the notifyManager to schedule the react notification
      // after the current render is complete. this is safe because the cache data
      // is already available immediately and only the React notification is deferred.
      notifyManager.schedule(() => {
        notificationScheduled = false
        listeners.forEach((notify) => notify())
      })
    }

    const subscribe = (listener: Listener) => {
      listeners.add(listener)

      if (!unsubscribeFromQueryCache) {
        // DO NOT MOVE this since we need to capture the initial snapshot before subscribing to the cache events.
        lastNotifiedSnapshot = getSnapshot()

        unsubscribeFromQueryCache = queryCache.subscribe((event) => {
          // QueryCache events fire for EVERY query so filter to ours first.
          if (event.query.queryHash !== queryHash) return

          const nextSnapshot = getSnapshot()

          // this check is used to avoid unnecessary re renders when the snapshot has not changed.
          // we use Object.is to compare the snapshots because it is the same comparison used by
          // React internally when comparing snapshots.
          if (Object.is(lastNotifiedSnapshot, nextSnapshot)) return

          // update synchronously so additional cache events can be compared
          // against the latest value while the React notification is pending.
          lastNotifiedSnapshot = nextSnapshot
          scheduleNotification()
        })
      }

      return () => {
        listeners.delete(listener)

        if (listeners.size === 0) {
          unsubscribeFromQueryCache?.()
          unsubscribeFromQueryCache = undefined
        }
      }
    }

    return { subscribe, getSnapshot }
  }

  type Store = ReturnType<typeof createStore>

  // one entry per QueryClient since SSR and CSR use different clients. stores are keyed by
  // query hash, and hashes are cached by queryKey reference so a static key is hashed once.
  const clients = new WeakMap<
    QueryClient,
    { stores: Map<string, Store>; hashes: WeakMap<QueryKey, string> }
  >()

  // called during render. this is idempotent per client and key, so this is safe.
  const getStore = (queryClient: QueryClient, options: TOptions): Store => {
    let client = clients.get(queryClient)
    if (!client) {
      client = { stores: new Map(), hashes: new WeakMap() }
      clients.set(queryClient, client)
    }

    let queryHash = client.hashes.get(options.queryKey)
    if (queryHash === undefined) {
      // resolve the exact hash react query uses for this key
      queryHash = queryClient.defaultQueryOptions(options).queryHash
      client.hashes.set(options.queryKey, queryHash)
    }

    let store = client.stores.get(queryHash)
    if (!store) {
      store = createStore(queryClient, queryHash)
      client.stores.set(queryHash, store)
    }

    return store
  }

  // reads the current data from the query cache without mounting a query observer.
  return (...args: TArgs): TData | undefined => {
    const queryClient = useQueryClient()
    const store = getStore(queryClient, getOptions(...args))

    // same reader for the SSR snapshot to avoid server/client mismatch.
    return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  }
}
